import '../reset.css';
import '../style.css';
import {userBrowserId} from '../scripts/common.js';
import {initIframeResize} from './_inIFrame.js';
import {
  QUIZ_BY_LANGUAGE,
  TEST_LANGUAGES,
  applyQuizAnswer,
  getCurrentQuestion,
  getInitialQuizState,
  validateQuizConfig,
} from './_preparewritten_quiz.js';

initIframeResize();

const UI_TEXT = {
  en: {
    chooseLanguage: 'Choose test language',
    configurationError: 'Configuration error',
    configurationErrorBody: 'The quiz is not ready yet.',
    question: 'Question',
    selectedLanguage: 'Test language',
    correct: 'Correct',
    incorrect: 'Try again',
    completedTitle: 'Preparation quiz completed',
    completedBody: 'You have answered all questions correctly.',
    restart: 'Restart',
    chooseAnotherLanguage: 'Choose another language',
  },
  ru: {
    chooseLanguage: 'Выберите язык теста',
    configurationError: 'Ошибка конфигурации',
    configurationErrorBody: 'Тест пока не готов.',
    question: 'Вопрос',
    selectedLanguage: 'Язык теста',
    correct: 'Верно',
    incorrect: 'Попробуйте ещё раз',
    completedTitle: 'Тест подготовки пройден',
    completedBody: 'Вы правильно ответили на все вопросы.',
    restart: 'Начать заново',
    chooseAnotherLanguage: 'Выбрать другой язык',
  },
};

let pageLang = 'en';
let root = null;
let questionHost = null;
let selectedLanguageId = null;
let quizState = null;

function t(key) {
  return UI_TEXT[pageLang]?.[key] || UI_TEXT.en[key] || key;
}

function createElement(tagName, className = '', text = '') {
  const node = document.createElement(tagName);
  if (className) {
    node.className = className;
  }
  if (text) {
    node.textContent = text;
  }
  return node;
}

function getLanguageLabel(languageId) {
  return TEST_LANGUAGES.find((item) => item.id === languageId)?.label || languageId;
}

function scrollToBottom() {
  const marker = document.getElementById('preparewritten-bottom');
  if (!marker) {
    return;
  }
  requestAnimationFrame(() => {
    marker.scrollIntoView({block: 'end', behavior: 'smooth'});
  });
}

function renderConfigError(errors) {
  const wrapper = createElement('section', 'rounded-lg border border-red-200 bg-red-50 p-5 text-red-950');
  const title = createElement('h2', 'font-robotoslab text-xl font-bold', t('configurationError'));
  const body = createElement('p', 'mt-2', t('configurationErrorBody'));
  const list = createElement('ul', 'mt-4 list-disc space-y-1 pl-5 text-sm');

  errors.forEach((message) => {
    list.appendChild(createElement('li', '', message));
  });

  wrapper.append(title, body, list);
  root.replaceChildren(wrapper);
}

function renderLanguageChooser() {
  selectedLanguageId = null;
  quizState = null;
  questionHost = null;

  const wrapper = createElement('section', 'space-y-6');
  const title = createElement('h2', 'font-robotoslab text-2xl font-bold text-[#023048]', t('chooseLanguage'));
  const grid = createElement('div', 'grid gap-3 sm:grid-cols-2');

  TEST_LANGUAGES.forEach((language) => {
    const button = createElement(
      'button',
      'min-h-16 rounded-lg border border-gray-300 bg-white px-5 py-4 text-left text-lg font-bold text-[#023048] shadow-sm transition hover:border-[#C1111E] hover:text-[#C1111E] focus:outline-none focus:ring-2 focus:ring-[#C1111E] focus:ring-offset-2',
      language.label,
    );
    button.type = 'button';
    button.addEventListener('click', () => startQuiz(language.id));
    grid.appendChild(button);
  });

  wrapper.append(title, grid);
  root.replaceChildren(wrapper);
}

function renderQuizShell() {
  const wrapper = createElement('section', 'space-y-6');
  const header = createElement('div', 'flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-center sm:justify-between');
  const languageInfo = createElement('div');
  const label = createElement('p', 'text-sm font-semibold uppercase tracking-wide text-gray-500', t('selectedLanguage'));
  const value = createElement('p', 'mt-1 text-2xl font-bold text-[#023048]', getLanguageLabel(selectedLanguageId));
  const changeButton = createElement(
    'button',
    'self-start rounded-md border border-gray-300 bg-white px-4 py-2 font-semibold text-[#023048] transition hover:border-[#C1111E] hover:text-[#C1111E] focus:outline-none focus:ring-2 focus:ring-[#C1111E] focus:ring-offset-2',
    t('chooseAnotherLanguage'),
  );

  changeButton.type = 'button';
  changeButton.addEventListener('click', renderLanguageChooser);
  languageInfo.append(label, value);
  header.append(languageInfo, changeButton);
  questionHost = createElement('div', 'space-y-5');
  wrapper.append(header, questionHost);
  root.replaceChildren(wrapper);
}

function renderQuestion() {
  const question = getCurrentQuestion(quizState);
  if (!question) {
    renderCompletion();
    return;
  }

  const total = QUIZ_BY_LANGUAGE[selectedLanguageId].length;
  const section = createElement('section', 'rounded-lg border border-gray-200 bg-white p-5 shadow-sm');
  const counter = createElement('p', 'text-sm font-bold uppercase tracking-wide text-[#C1111E]', `${t('question')} ${quizState.questionIndex + 1} / ${total}`);
  const title = createElement('h2', 'mt-3 whitespace-pre-line font-robotoslab text-2xl font-bold leading-snug text-[#023048]', question.question);
  const options = createElement('div', 'mt-5 space-y-3');
  const optionButtons = [];

  question.options.forEach((option, optionIndex) => {
    const button = createElement(
      'button',
      'block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-left font-semibold text-gray-900 transition hover:border-[#023048] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#023048] focus:ring-offset-2 disabled:cursor-default disabled:hover:bg-white',
      option.text,
    );
    button.type = 'button';
    button.addEventListener('click', () => handleAnswer(section, optionButtons, optionIndex));
    optionButtons.push(button);
    options.appendChild(button);
  });

  section.append(counter, title, options);
  questionHost.appendChild(section);
  scrollToBottom();
}

function handleAnswer(section, optionButtons, optionIndex) {
  if (section.dataset.locked === '1') {
    return;
  }
  section.dataset.locked = '1';

  const result = applyQuizAnswer(quizState, optionIndex);
  quizState = result.state;

  optionButtons.forEach((button, index) => {
    button.disabled = true;
    button.classList.add('opacity-70');
    if (index === optionIndex) {
      button.classList.add(result.correct ? 'border-green-600' : 'border-red-500');
      button.classList.add(result.correct ? 'bg-green-50' : 'bg-red-50');
    }
  });

  const feedback = createElement(
    'div',
    result.correct
      ? 'mt-5 rounded-lg border border-green-200 bg-green-50 p-4 text-green-950'
      : 'mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-red-950',
  );
  const feedbackTitle = createElement('p', 'font-bold', result.correct ? t('correct') : t('incorrect'));
  const feedbackBody = createElement('p', 'mt-2 whitespace-pre-line', result.option.answer);
  feedback.append(feedbackTitle, feedbackBody);
  section.appendChild(feedback);

  if (quizState.completed) {
    renderCompletion();
  } else {
    renderQuestion();
  }
}

function renderCompletion() {
  const section = createElement('section', 'rounded-lg border border-green-200 bg-green-50 p-5 text-green-950');
  const title = createElement('h2', 'font-robotoslab text-2xl font-bold', t('completedTitle'));
  const body = createElement('p', 'mt-2', t('completedBody'));
  const actions = createElement('div', 'mt-5 flex flex-col gap-3 sm:flex-row');
  const restartButton = createElement('button', 'rounded-md bg-[#C1111E] px-5 py-3 font-bold text-white transition hover:bg-[#770101] focus:outline-none focus:ring-2 focus:ring-[#C1111E] focus:ring-offset-2', t('restart'));
  const chooserButton = createElement('button', 'rounded-md border border-gray-300 bg-white px-5 py-3 font-bold text-[#023048] transition hover:border-[#C1111E] hover:text-[#C1111E] focus:outline-none focus:ring-2 focus:ring-[#C1111E] focus:ring-offset-2', t('chooseAnotherLanguage'));

  restartButton.type = 'button';
  chooserButton.type = 'button';
  restartButton.addEventListener('click', () => startQuiz(selectedLanguageId));
  chooserButton.addEventListener('click', renderLanguageChooser);
  actions.append(restartButton, chooserButton);
  section.append(title, body, actions);
  questionHost.appendChild(section);
  scrollToBottom();
}

function startQuiz(languageId) {
  selectedLanguageId = languageId;
  quizState = getInitialQuizState(languageId);
  renderQuizShell();
  renderQuestion();
}

function initPrepareWritten() {
  root = document.getElementById('preparewritten-app');
  if (!root) {
    return;
  }

  pageLang = document.documentElement.lang === 'ru' ? 'ru' : 'en';
  const validation = validateQuizConfig();
  if (!validation.ok) {
    renderConfigError(validation.errors);
    return;
  }

  renderLanguageChooser();
}

document.addEventListener('DOMContentLoaded', initPrepareWritten);
